import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { NotificationService } from '../../main/notification.service';
import { SETTINGS_RELOAD_DELAY_MS } from '../../app.constants';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import { PropertyComponent } from '../../main/property/property.component';
import { Cpms, SettingWithMetadata } from '../../models';
import { ChargerSettingsService } from '../charger-settings.service';
import { ChargersService } from '../chargers.service';


@Component({
	selector: 'app-charger-settings-installer-ocpp',
	imports: [MatListModule, MatProgressSpinnerModule, MatIconModule, MatFormFieldModule, MatSelectModule, MatOptionModule, MatInputModule, PropertyComponent, MatCardModule],
	templateUrl: './charger-settings-installer-ocpp.component.html',
	styleUrl: './charger-settings-installer-ocpp.component.scss'
})
export class ChargerSettingsInstallerOcppComponent implements OnInit {
	chargerId!: string;
	chargerProperties = signal<SettingWithMetadata[]>([]);
	chargerPropertiesLoaded = signal<boolean>(false);
	cpmsList: Cpms[] = [];

	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private chargersService = inject(ChargersService);
	private chargerSettingsService = inject(ChargerSettingsService);
	private notificationService = inject(NotificationService);
	private destroyRef = inject(DestroyRef);

	async ngOnInit() {
		this.chargerId = this.route.parent?.snapshot.paramMap.get('id') ?? '';
		const isValid = /^[PD]\d{14}$/.test(this.chargerId);
		if (!isValid) {
			this.router.navigate(['/404']);
		}

		this.cpmsList = (await firstValueFrom(this.chargersService.getChargerConfigOcppCpms(this.chargerId)))
			.sort((a, b) => a.name.localeCompare(b.name));
		console.log('getChargerConfigOcppCpms():', this.cpmsList);
		
		this.loadChargerSettings();
	}

	loadChargerSettings() {
		this.chargersService.getChargerSettingsInstallerOcpp(this.chargerId).pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe({
			next: (charger) => {
				console.log('getChargerSettingsInstallerOcpp():', charger);
				charger.cpms.allowedValues = this.cpmsList;

				const centralSystem = charger.cpms.value?.centralSystem;
				const matchedCpms = this.cpmsList.find(cpms => cpms.name === centralSystem);
				charger.chargePointIdentifier ??= { value: '', isChangeAllowed: false };
				charger.chargePointIdentifier.isChangeAllowed = (matchedCpms?.cpidType === 'custom') || (!!centralSystem && !matchedCpms);

				this.chargerProperties.set([
					{
						id: 'enabled',
						name: $localize`:@@Enabled:Enabled`,
						valueWithMetadata: charger.enabled,
						type: 'boolean'
					},
					{
						id: 'cpms',
						name: $localize`:@@ChargePointOperator:Charge Point Operator`,
						valueWithMetadata: charger.cpms,
						type: 'Cpms'
					},
					{
						id: 'chargePointIdentifier',
						name: $localize`:@@ChargePointIdentifier:Charge Point Identifier`,
						valueWithMetadata: charger.chargePointIdentifier,
						type: 'freeString'
					},
				]);
				console.log('getChargerSettingsInstallerOcpp() chargerProperties:', this.chargerProperties());
				this.chargerPropertiesLoaded.set(true);
			},
			error: (err) => {
				console.error('getChargerSettingsInstallerOcpp() error:', err);
				this.notificationService.openSnackbar(false, err.error.errorMessage, $localize`:@@Load:Load`);
				this.chargerPropertiesLoaded.set(true);
			},
		});
	}


	saveCallback = (setting: SettingWithMetadata) => this.saveSetting(setting); // for preserving `this` context in `saveSetting`

	saveSetting(setting: SettingWithMetadata): Observable<boolean> {
		console.log('saveSetting():', setting);
		let value;
		if (setting.valueWithMetadata) {
			if (setting.id === 'cpms') {
				let valueWithMetadata = this.chargerSettingsService.returnCpmsWithMetaData(setting.valueWithMetadata);
				if ((valueWithMetadata) && (valueWithMetadata.value)) {
					let selectedCpms = this.cpmsList.find(cpms => cpms.name === valueWithMetadata.value?.centralSystem);
					if (selectedCpms) {
						valueWithMetadata.value.url = selectedCpms.url;
						value = valueWithMetadata.value;
					} else if (valueWithMetadata.value?.centralSystem && valueWithMetadata.value?.url) {
						// Custom CPMS – use name and URL as entered manually
						value = valueWithMetadata.value;
					}

				}
			} else {
				if ('value' in setting.valueWithMetadata) {
					value = setting.valueWithMetadata?.value;
				}
			}
		}
		if (setting && value !== null) {
			return this.chargersService.putChargerSettingsInstallerOcpp(this.chargerId, setting.id, value).pipe(
				map(result => {
					if ((setting.id === 'enabled') || (setting.id === 'cpms')) {
						setTimeout(() => this.loadChargerSettings(), SETTINGS_RELOAD_DELAY_MS); // reload because isChangeAllowed can be changed, and cpms change updates chargePointIdentifier
					}
					return this.notificationService.openSnackbar(result.success, result.errorMessage);
				}),
				catchError(err => {
					return of(this.notificationService.openSnackbar(false, err.message));
				})			
			);
		}
		console.log('saveSetting() skipped — setting or value invalid:', setting, value);
		return of(false);
	}
}
